import React from 'react';
import styled from 'styled-components';

import { useDispatch, useSelector } from 'react-redux';
import AppState from '../../../store/state';
import MotionOpacity from '../../../components/motion/motion-opacity';
import Typography from '../../../components/basics/typography';
import { Translate } from 'react-localize-redux';
import { GetOwnerDeals } from '../../../store/deals/action-creators';
import { selectAdvanceDeals, selectLabelDeals } from '../../../store/deals/selectors';
import { AdvanceDealsSummaryCtrl } from '../../../controllers/label/deal-management/summary/advance';
import { DealList } from '../../../controllers/label/deal-management/components/deal-list';
import { LABEL_DEAL_COLUMNS } from '../../../controllers/label/deal-management/parsers/label-deals';
import { LabelDealsSummaryCtrl } from '../../../controllers/label/deal-management/summary/labels';
import { ADVANCE_DEAL_COLUMNS } from '../../../controllers/label/deal-management/parsers/advance-deals';
import { Deal } from '../../../../../../services/deals/types/deal';
import Flex from '../../../components/layout/flex';
import Avatar from '../../../components/basics/avatar';

interface LabelDealsPageProps {
  className?: string;
}

const renderLabelChild = (deal: Deal) => {
  return (
    <DealLabelWrapper>
      {deal.artists?.map((artist) => (
        <DealLabelArtist key={artist.id} gap={10} alignItems='center'>
          <Avatar src={artist.avatarUrl} />
          <Typography weight='700'>{artist.name}</Typography>
        </DealLabelArtist>
      ))}
    </DealLabelWrapper>
  )
}

const renderAdvanceChild = (deal: Deal) => {
  if (deal.isrcs?.length) {
    return (
      <DealLabelWrapper>
        {deal.isrcs?.map((isrc) => (
          <DealLabelArtist key={`${isrc.isrc} - ${isrc.version}`} gap={10} alignItems='center'>
            <Avatar src={isrc.coverArtUrl} />
            <Typography weight='700'>{isrc.isrc} {isrc.title} {isrc.version ? `(${isrc.version})` : ''}</Typography>
          </DealLabelArtist>
        ))}
      </DealLabelWrapper>
    )
  }

  if (deal.artists.length) {
    return (
      <DealLabelWrapper>
        {deal.artists?.map((artist) => (
          <DealLabelArtist key={artist.id} gap={10} alignItems='center'>
            <Avatar src={artist.avatarUrl} />
            <Typography weight='700'>{artist.name}</Typography>
          </DealLabelArtist>
        ))}
      </DealLabelWrapper>
    )
  }

  return null;
}

export const LabelDealsPage: React.FC<LabelDealsPageProps> = ({ className }) => {
  const dispatch = useDispatch();

  const isMobile = useSelector((state: AppState) => !!state.ui.mobile);
  const labelId = useSelector((state: AppState) => state.recordLabels.active?.id);
  const dealsEnabled = useSelector((state: AppState) => !!state.recordLabels.active?.flags?.deals);

  const labelDeals = useSelector(selectLabelDeals);
  const advanceDeals = useSelector(selectAdvanceDeals);

  React.useEffect(() => {
    if (labelId) {
      dispatch(new GetOwnerDeals(labelId));
    }
  }, [dispatch, labelId]);

  if (!labelId || !dealsEnabled) {
    return null;
  }

  return (
    <MotionOpacity className={className}>
      <GridWrapper>
        <Typography type="subheader" customFont="semi-bold" className="mb-30">
          <Translate id="LABEL.DEALS.TITLE" />
        </Typography>

        {advanceDeals.length ? (
          <SectionWrapper>
            <Typography type="title" customFont="semi-bold" className="mb-30">
              <Translate id="LABEL.DEALS.TITLE.ADVANCE" />
            </Typography>
            <AdvanceDealsSummaryCtrl deals={advanceDeals} />
            <DealList
              deals={advanceDeals}
              columns={ADVANCE_DEAL_COLUMNS}
              isMobile={isMobile}
              childRender={renderAdvanceChild}
            />
          </SectionWrapper>
        ) : null}
        
        {labelDeals.length ? (
          <SectionWrapper>
            <Typography type="title" customFont="semi-bold" className="mb-30">
              <Translate id="LABEL.DEALS.TITLE.LABELS" />
            </Typography>
            <LabelDealsSummaryCtrl deals={labelDeals} />
            <DealList
              deals={labelDeals}
              columns={LABEL_DEAL_COLUMNS}
              isMobile={isMobile}
              childRender={renderLabelChild}
            />
          </SectionWrapper>
        ) : null}

        {/* <SectionWrapper>
          <DealManagementCtrl deals={labelDeals} title={<Translate id="LABEL.DEALS.TITLE.ARTISTS" />} />
        </SectionWrapper> */}
      </GridWrapper>
    </MotionOpacity>
  );
};

const GridWrapper = styled.div`
  display: grid;
  grid-gap: 3px;
`;

const SectionWrapper = styled.div`
  margin-bottom: 3rem;
`

const DealLabelArtist = styled(Flex)`
  margin-top: 1rem;
`

const DealLabelWrapper = styled.div`
  padding-left: 3rem;
  margin-bottom: 2rem;
`